/**
 * @file: preshing-date-format.pipe.ts
 * @since: 7-Jul-2017
 * @desc: pershingDateFormat pipe is used to dislay the timeZone in short format which is not available
 *        current format options available for datePipe.
 */

import { Pipe , PipeTransform } from '@angular/core';
import * as moment from 'moment-timezone';
import { DateTimeFormat, DefactoDateFormat } from '../constants/date-format-constants';
import { PershingLocaleService } from '../services/pershing-locale.service';
/**
 * @ngModule NxpSharedModule
 * @whatItDoes Format the given date according to the standard patterns
 * @howToUse: {{ output | pershingDateFormat:'pattern':'displayTimezone' }}
 * @desc
 *  `'pattern'` - the predefined standard pattern
 *  `displayTimezone` - takes boolen to display timezone as a part of the time stamp or not
 *  - 'long' is equivalent to `MMMM  D, YYYY h:mm A `
 *  - 'medium' is equivalent to `MMM D, YYYY h:mm A`
 *  - 'short' equivalent to ` M/DD/YYYY h:mm A `
 *  - 'longDate' equivalent to `MMMM DD, YYYY`
 *  - 'mediumDate' equivalent to `MMM D, YYYY`
 *  - 'shortDate' equivalent to ` M/DD/YYYY`
 *  - 'longTime' equivalent to `h:mm:ss A`
 *  - 'shortTime' equivalent to `h:mm A`
 *  @reference https://momentjs.com/docs/#/displaying/
 */

@Pipe({
    name: 'pershingDateFormat',
    pure: true
})
export class PershingDateFormatPipe implements PipeTransform {
    private defaultDateFormat: string = DateTimeFormat.MEDIUM;

    private pershingDateTimePatterns: any = [
                                { format: DateTimeFormat.LONG, displayTimezone: true },
                                { format: DateTimeFormat.MEDIUM, displayTimezone: true },
                                { format: DateTimeFormat.SHORT, displayTimezone: true },
                                { format: DateTimeFormat.LONG_DATE, displayTimezone: false },
                                { format: DateTimeFormat.MEDIUM_DATE, displayTimezone: false },
                                { format: DateTimeFormat.SHORT_DATE, displayTimezone: false },
                                { format: DateTimeFormat.LONG_TIME, displayTimezone: true },
                                { format: DateTimeFormat.SHORT_TIME, displayTimezone: true }
                            ];
    private defactoDatePatterns: any = [DefactoDateFormat.MONTH_YEAR,
                                        DefactoDateFormat.SHORT_MONTH_YEAR,
                                        DefactoDateFormat.SHORT_MONTH_DATE,
                                        DefactoDateFormat.SHORT_MONTH,
                                        DefactoDateFormat.DATE,
                                        DefactoDateFormat.YEAR,
                                        DefactoDateFormat.LONG_YEAR_MONTH_DATE
                                        ];
    private timeZoneFormat: string = 'z';
    private defaultLocale: string = 'en';
    private defaultTimeZone: string = 'America/New_York';
    private destinationDateFormat: string;
    private dateTimeResponse: string = null;
    private isTimeZoneApplicable: boolean = false;
    // source dateTime properties
    private sourceDateTime: any;
    private sourceTimeFormat: string;
    private sourceTimeZone: string;

    constructor(private localeService: PershingLocaleService) {
    }
    /**
     *
     * @param value - the actual input to be formatted
     * @param (optional) pattern - a standard pattern from 'DateTimeFormat'
     * @param (optional)displaytimezone - takes a boolean to display timezone for the given input or not
     * @param (optional)sourceTimeZone - source timezone for the input and defaultTimeZone will be taken if not passed
     * @param (optional)sourceDateTimeFormat
     * @return formatted dateTime based on the provided inputs
     */

    transform(value: any, pattern?: string, displaytimezone?: boolean, sourceDateTimeFormat?: string, sourceTimeZone?: string ): string | null  {
        if ( this.validateInput(value, pattern, displaytimezone, sourceDateTimeFormat, sourceTimeZone ) ) {
            /** create the sourceDateTime based the sourceTimeZone and format
             * source timezone is defaulted to 'America/New_York' if not passed as a parameter
             */
            this.sourceDateTime = moment.tz(value, this.sourceTimeFormat, this.sourceTimeZone).format();
            /**
             * - append the timezone string if it has to be displayed based on the parameter to the pipe
             * - timezone should be displayed only for valid format
             */
            this.destinationDateFormat = (this.isTimeZoneApplicable && displaytimezone ) ? this.destinationDateFormat + ' ' + this.timeZoneFormat : this.destinationDateFormat;

            this.dateTimeResponse =  moment.tz(this.sourceDateTime, this.defaultTimeZone).locale(this.localeService.getCurrentLocale()).format(this.destinationDateFormat);

        }
        return this.dateTimeResponse;
    }

    /**
     * @desc validates whether the given pattern
     * @param inputPattern
     * @return a boolean
     */
    private isPatternValid(inputPattern: string): boolean {
        let isPatternValid: boolean = false;
        if (typeof inputPattern !== 'undefined') {
            this.pershingDateTimePatterns.forEach(element => {
                if (element.format === inputPattern ) {
                    this.isTimeZoneApplicable = element.displayTimezone;
                    isPatternValid = true;
                }
            });
        }
        return isPatternValid;
    }

    /**
     *
     * @param value
     * @param pattern
     * @param isDisplayTimezone
     * @param sourceTimeZone
     * @param sourceDateTimeFormat
     */
    private validateInput(value, pattern, isDisplayTimezone, sourceDateTimeFormat, sourceTimeZone): boolean {
        let isValidInput: boolean = false;
        this.destinationDateFormat = this.defaultDateFormat;
        if ( (typeof value !== 'undefined') && value ) {
            if ( pattern ) {
                if (this.isPatternValid(pattern)) {
                    this.destinationDateFormat = pattern;
                    isValidInput = true;
                }
            } else {
                isValidInput = true;
            }
            this.sourceTimeFormat = (!(typeof sourceDateTimeFormat === 'undefined') && sourceDateTimeFormat) ? sourceDateTimeFormat : '' ;
            this.sourceTimeZone = (!(typeof sourceTimeZone === 'undefined') && sourceTimeZone) ? sourceTimeZone : this.defaultTimeZone ;
        }
        return isValidInput;
    }

    /**
     * Format to non standard pattern
     * @param value - the actual input to be formatted
     * @param pattern - a non-standard pattern from 'DefactoDateFormat'
     * @param (optional)sourceTimeZone - source timezone for the input and defaultTimeZone will be taken if not passed
     * @param (optional)sourceDateTimeFormat
     * @return formatted dateTime based on the provided inputs
     */
    format(value: any, pattern: string, sourceDateTimeFormat?: string, sourceTimeZone?: string ): string | null  {
        this.dateTimeResponse = null;
        if ( this.validateDefactoInput(value, pattern, sourceDateTimeFormat, sourceTimeZone ) ) {
            /** create the sourceDateTime based the sourceTimeZone and format
             * source timezone is defaulted to 'America/New_York' if not passed as a parameter
             */
            this.sourceDateTime = moment.tz(value, this.sourceTimeFormat, this.sourceTimeZone).format();
            this.dateTimeResponse =  moment.tz(this.sourceDateTime, this.defaultTimeZone).locale(this.localeService.getCurrentLocale()).format(this.destinationDateFormat);

        }
        return this.dateTimeResponse;
    }

    /**
     * @desc validates non-standard whether the given pattern
     * @param inputPattern
     * @return a boolean
     */
    private isDefactoPatternValid(inputPattern: string): boolean {
        let isPatternValid: boolean = false;
        if (typeof inputPattern !== 'undefined') {
            this.defactoDatePatterns.forEach(pattern => {
                if (pattern === inputPattern ) {
                    isPatternValid = true;
                }
            });
        }
        return isPatternValid;
    }

    /**
     *
     * @param value
     * @param pattern
     * @param sourceTimeZone
     * @param sourceDateTimeFormat
     */
    private validateDefactoInput(value, pattern, sourceDateTimeFormat, sourceTimeZone): boolean {
        let isValidInput: boolean = false;
        if ((typeof value !== 'undefined') && value) {
            if (this.isDefactoPatternValid(pattern)) {
                this.destinationDateFormat = pattern;
                isValidInput = true;
            }
            this.sourceTimeFormat = (!(typeof sourceDateTimeFormat === 'undefined') && sourceDateTimeFormat) ? sourceDateTimeFormat : '';
            this.sourceTimeZone = (!(typeof sourceTimeZone === 'undefined') && sourceTimeZone) ? sourceTimeZone : this.defaultTimeZone;
        }
        return isValidInput;
    }
}
